// Serverdaten fuer Phase 2: Meine Woche (Pool + Kalender) und Einkaufen.
// Jasmin und Bernd teilen sich Woche und Liste. Damit Aenderungen des anderen ohne Neuladen ankommen,
// fragen beide Abfragen alle 10 s nach, solange die App sichtbar ist (TanStack pausiert im Hintergrund),
// und sofort beim Zurueckkehren in die App (refetchOnWindowFocus). Kein Realtime-Dienst noetig.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  eintraegeLesen,
  eintragErledigt,
  eintragVormerken,
  erledigt,
  istNetzfehler,
  lesen,
  mitWarteschlange,
  mitZustaenden,
  neueId,
  vormerken,
  WARTESCHLANGE_EREIGNIS,
} from './offline';
import { api, type EinkaufListe, type EinkaufPosten, type EinkaufRezept, type Woche, type WochenEintrag } from './client';
import { useNutzerId } from './rezepte';
import type { components } from './schema.gen';

export const NACHFRAGEN_MS = 10_000;

export const wocheSchluessel = {
  alle: (n: string) => ['nutzer', n, 'woche'] as const,
  zeitraum: (n: string, von: string, bis: string) => ['nutzer', n, 'woche', von, bis] as const,
  einkauf: (n: string) => ['nutzer', n, 'einkauf'] as const,
};

/** Woche ab Montag. Ohne Zeitraum: die laufende Woche (Server rechnet "heute" in Europe/Berlin). */
export function useWoche(zeitraum?: { von: string; bis: string }) {
  const n = useNutzerId();
  const params = zeitraum ? `?von=${zeitraum.von}&bis=${zeitraum.bis}` : '';
  return useQuery({
    queryKey: zeitraum ? wocheSchluessel.zeitraum(n, zeitraum.von, zeitraum.bis) : [...wocheSchluessel.alle(n), 'jetzt'],
    queryFn: () => api<Woche>(`/woche${params}`),
    enabled: n !== 'abgemeldet',
    staleTime: 5_000,
    refetchInterval: NACHFRAGEN_MS,
  });
}

function useWocheNeuLaden() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: wocheSchluessel.alle(n) }),
      // "In Meine Woche"-Status im Rezeptdetail
      qc.invalidateQueries({ queryKey: ['nutzer', n, 'rezept'] }),
    ]);
}

export type EintragAenderung = { datum?: string | null; gekocht?: boolean; portionen?: number };

export function useEintragAendern() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & EintragAenderung) =>
      api<WochenEintrag>(`/woche/eintraege/${id}`, { method: 'PATCH', body: JSON.stringify(daten) }),
    // Sofort sichtbar: in allen geladenen Wochen den Eintrag umschreiben
    onMutate: async ({ id, ...daten }) => {
      await qc.cancelQueries({ queryKey: wocheSchluessel.alle(n) });
      const vorher = qc.getQueriesData<Woche>({ queryKey: wocheSchluessel.alle(n) });
      qc.setQueriesData<Woche>({ queryKey: wocheSchluessel.alle(n) }, (w) =>
        w
          ? {
              ...w,
              eintraege: w.eintraege.map((e) =>
                e.id !== id
                  ? e
                  : {
                      ...e,
                      ...('datum' in daten ? { datum: daten.datum ?? null } : {}),
                      ...(daten.gekocht !== undefined
                        ? { gekocht_am: daten.gekocht ? new Date().toISOString() : null, datum: e.datum ?? (daten.gekocht ? w.heute : null) }
                        : {}),
                    },
              ),
            }
          : w,
      );
      return { vorher };
    },
    onError: (_f, _d, ctx) => ctx?.vorher.forEach(([k, d]) => qc.setQueryData(k, d)),
    onSettled: () => {
      void neuLaden();
    },
  });
}

export function useEintragEntfernen() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: (id: string) => api<null>(`/woche/eintraege/${id}`, { method: 'DELETE' }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: wocheSchluessel.alle(n) });
      const vorher = qc.getQueriesData<Woche>({ queryKey: wocheSchluessel.alle(n) });
      qc.setQueriesData<Woche>({ queryKey: wocheSchluessel.alle(n) }, (w) =>
        w ? { ...w, eintraege: w.eintraege.filter((e) => e.id !== id) } : w,
      );
      return { vorher };
    },
    onError: (_f, _d, ctx) => ctx?.vorher.forEach(([k, d]) => qc.setQueryData(k, d)),
    onSettled: () => {
      void neuLaden();
    },
  });
}

export type Geleert = components['schemas']['Geleert'];
export type GesicherterEintrag = components['schemas']['GesicherterEintrag'];

/** "Woche leeren" (QA Runde 2 N6): nur die angezeigte Woche, die Auswahl nach Wahl; Antwort traegt den Stand fuers Rueckgaengig. */
export function useWocheLeeren() {
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: (d: { von: string; bis: string; auswahl: boolean }) =>
      api<Geleert>('/woche/leeren', { method: 'POST', body: JSON.stringify(d) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

export function useWocheWiederherstellen() {
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: (eintraege: GesicherterEintrag[]) =>
      api<{ angelegt: number }>('/woche/wiederherstellen', { method: 'POST', body: JSON.stringify(eintraege) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

// ---------------------------------------------------------------- Einkaufen

export function useEinkauf() {
  const n = useNutzerId();
  return useQuery({
    queryKey: wocheSchluessel.einkauf(n),
    // Offline Abgehaktes, das noch nicht beim Server ist, liegt ueber der Liste (auch der aus dem Cache)
    queryFn: async () => mitWarteschlange(await api<EinkaufListe>('/einkauf'), n),
    enabled: n !== 'abgemeldet',
    staleTime: 5_000,
    refetchInterval: NACHFRAGEN_MS,
  });
}

function useEinkaufNeuLaden() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: wocheSchluessel.einkauf(n) }),
      qc.invalidateQueries({ queryKey: ['nutzer', n, 'rezept'] }),
    ]);
}

export function useAbhaken() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    // Auch ohne Netz ausfuehren: dann landet die Aenderung in der Warteschlange (api/offline.ts)
    networkMode: 'always',
    mutationFn: async (daten: { posten_ids: string[]; abgehakt: boolean }) => {
      try {
        return { ...(await api<{ geaendert: number }>('/einkauf/abhaken', { method: 'POST', body: JSON.stringify(daten) })), vorgemerkt: false };
      } catch (f) {
        if (!istNetzfehler(f)) throw f;
        vormerken(n, daten.posten_ids, daten.abgehakt);
        return { geaendert: 0, vorgemerkt: true };
      }
    },
    onMutate: async ({ posten_ids, abgehakt }) => {
      await qc.cancelQueries({ queryKey: wocheSchluessel.einkauf(n) });
      const vorher = qc.getQueryData<EinkaufListe>(wocheSchluessel.einkauf(n));
      if (vorher) qc.setQueryData(wocheSchluessel.einkauf(n), mitZustaenden(vorher, new Map(posten_ids.map((id) => [id, abgehakt]))));
      return { vorher };
    },
    onError: (_f, _d, ctx) => {
      if (ctx?.vorher) qc.setQueryData(wocheSchluessel.einkauf(n), ctx.vorher);
    },
    onSettled: (ergebnis) => {
      if (!ergebnis?.vorgemerkt) void neuLaden();
    },
  });
}

/** Traegt offline Abgehaktes nach: beim Start, wenn das Netz zurueckkommt und solange etwas wartet alle 10 s. */
export function useAbhakenNachtragen(): number {
  const n = useNutzerId();
  const qc = useQueryClient();
  const [wartend, setWartend] = useState(() => Object.keys(lesen(n)).length + eintraegeLesen(n).length);
  useEffect(() => {
    if (n === 'abgemeldet') return;
    let laeuft = false;
    const zaehlen = () => setWartend(Object.keys(lesen(n)).length + eintraegeLesen(n).length);
    const nachtragen = async () => {
      const w = lesen(n);
      const eintraege = eintraegeLesen(n);
      if (laeuft || (Object.keys(w).length === 0 && eintraege.length === 0) || navigator.onLine === false) return;
      laeuft = true;
      try {
        // Eigene Eintraege zuerst (QA Runde 2 N1), mit ihrer Geraete-ID: doppelt senden legt keine zweite Zeile an
        for (const e of eintraege) {
          try {
            await api('/einkauf/posten', { method: 'POST', body: JSON.stringify({ name: e.name, id: e.id }) });
          } catch (f) {
            if (istNetzfehler(f)) return;
            // 4xx/5xx mit Antwort (Name ungueltig, ID vergeben): nicht ewig wiederholen
          }
          eintragErledigt(n, e.id);
        }
        for (const wert of [true, false]) {
          const ids = Object.entries(w).filter(([, v]) => v === wert).map(([id]) => id);
          // in Haeppchen zu 200 (Grenze des Endpunkts)
          for (let i = 0; i < ids.length; i += 200) {
            const teil = ids.slice(i, i + 200);
            try {
              await api('/einkauf/abhaken', { method: 'POST', body: JSON.stringify({ posten_ids: teil, abgehakt: wert }) });
            } catch (f) {
              if (istNetzfehler(f)) return;
              // 4xx/5xx mit Antwort: diese Zeilen gibt es so nicht mehr, nicht ewig wiederholen
            }
            erledigt(n, Object.fromEntries(teil.map((id) => [id, wert])));
          }
        }
        await qc.invalidateQueries({ queryKey: wocheSchluessel.einkauf(n) });
      } finally {
        laeuft = false;
        zaehlen();
      }
    };
    zaehlen();
    void nachtragen();
    const takt = window.setInterval(() => void nachtragen(), NACHFRAGEN_MS);
    const online = () => void nachtragen();
    window.addEventListener('online', online);
    window.addEventListener(WARTESCHLANGE_EREIGNIS, zaehlen);
    return () => {
      window.clearInterval(takt);
      window.removeEventListener('online', online);
      window.removeEventListener(WARTESCHLANGE_EREIGNIS, zaehlen);
    };
  }, [n, qc]);
  return wartend;
}

/** Eigene Eintraege, die ohne Netz auf das Senden warten (QA Runde 2 N1): aus localStorage, ueberleben Neuladen. */
export function useWartendeEintraege(): string[] {
  const n = useNutzerId();
  const [liste, setListe] = useState(() => eintraegeLesen(n).map((e) => e.name));
  useEffect(() => {
    const lesenNeu = () => setListe(eintraegeLesen(n).map((e) => e.name));
    lesenNeu();
    window.addEventListener(WARTESCHLANGE_EREIGNIS, lesenNeu);
    window.addEventListener('storage', lesenNeu);
    return () => {
      window.removeEventListener(WARTESCHLANGE_EREIGNIS, lesenNeu);
      window.removeEventListener('storage', lesenNeu);
    };
  }, [n]);
  return liste;
}

export function useFreierEintrag() {
  const n = useNutzerId();
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    // Auch ohne Netz ausfuehren: dann landet der Eintrag in der Warteschlange (api/offline.ts)
    networkMode: 'always',
    mutationFn: async (name: string) => {
      const id = neueId();
      try {
        return { posten: await api<EinkaufPosten>('/einkauf/posten', { method: 'POST', body: JSON.stringify({ name, id }) }), vorgemerkt: false };
      } catch (f) {
        if (!istNetzfehler(f)) throw f;
        eintragVormerken(n, { id, name });
        return { posten: null, vorgemerkt: true };
      }
    },
    onSettled: (ergebnis) => {
      if (!ergebnis?.vorgemerkt) void neuLaden();
    },
  });
}

export function useFreienEintragEntfernen() {
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    mutationFn: (id: string) => api<null>(`/einkauf/posten/${id}`, { method: 'DELETE' }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

/** Stand eines Rezepts auf der Liste beim Entfernen; "Rueckgaengig" legt ihn wieder an (QA W9 Befund 4). */
export interface GesichertesEinkaufRezept {
  rezept_id: string;
  portionen: number;
  posten: Array<{
    zutat_id: string | null;
    name: string;
    menge: number | null;
    einheit: string | null;
    einkaufskategorie: string | null;
    position: number;
    abgehakt: boolean;
  }>;
}

export function useEinkaufRezeptEntfernen() {
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    mutationFn: (id: string) => api<GesichertesEinkaufRezept>(`/einkauf/rezepte/${id}`, { method: 'DELETE' }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

export function useEinkaufRezeptWiederherstellen() {
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    mutationFn: (stand: GesichertesEinkaufRezept) =>
      api<EinkaufRezept>('/einkauf/rezepte/wiederherstellen', { method: 'POST', body: JSON.stringify(stand) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

export function useEinkaufAufraeumen() {
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    mutationFn: (art: 'erledigt' | 'alles') =>
      api<{ rezepte: number; freie: number }>('/einkauf/aufraeumen', { method: 'POST', body: JSON.stringify({ art }) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

/** Portionen eines Rezepts auf der Liste aendern (W7a). Mengen rechnet der Server aus dem Rezept neu. */
export function useEinkaufPortionen() {
  const neuLaden = useEinkaufNeuLaden();
  return useMutation({
    mutationFn: (d: { id: string; portionen: number }) =>
      api<EinkaufRezept>(`/einkauf/rezepte/${d.id}`, { method: 'PATCH', body: JSON.stringify({ portionen: d.portionen }) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

// ---------------------------------------------------------------- Kochmodus (W8)

export type GekochtAntwort = components['schemas']['GekochtAntwort'];

/** "Als gekocht markieren" am Ende des Kochmodus (W3-Logik im Backend, POST /woche/gekocht). */
export function useAlsGekocht() {
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: (daten: { rezept_id: string; portionen?: number }) =>
      api<GekochtAntwort>('/woche/gekocht', { method: 'POST', body: JSON.stringify(daten) }),
    onSettled: () => {
      void neuLaden();
    },
  });
}

/** Rueckgaengig: neu angelegt = loeschen, sonst gekocht zuruecknehmen und das alte Datum setzen. */
export function useGekochtRueckgaengig() {
  const neuLaden = useWocheNeuLaden();
  return useMutation({
    mutationFn: async (a: GekochtAntwort) => {
      if (a.vorher === 'neu') return api<null>(`/woche/eintraege/${a.eintrag_id}`, { method: 'DELETE' });
      return api<WochenEintrag>(`/woche/eintraege/${a.eintrag_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ gekocht: false, datum: a.datum_vorher ?? null }),
      });
    },
    onSettled: () => {
      void neuLaden();
    },
  });
}
